import { Patient } from 'fhir/r4';

/**
 * Filters out empty telecom entries, identifiers, and guardian contacts from a Patient resource.
 * Mirrors the cleansing logic originally implemented inside PatientRegistrationComponent so it
 * can be reused anywhere we mutate Patient resources (e.g., edit dialogs, profile updates).
 */
export function sanitizePatientResource(patient: Patient | undefined): void {
  if (!patient) {
    return;
  }

  if (Array.isArray(patient.telecom)) {
    patient.telecom = patient.telecom.filter(tp => hasContactPointValue(tp?.value));
  }

  if (Array.isArray(patient.identifier)) {
    patient.identifier = patient.identifier.filter(id => hasContactPointValue(id?.value));
  }

  if (Array.isArray(patient.contact)) {
    patient.contact = patient.contact
      .map(contact => sanitizeContactEntry(contact))
      .filter(contact => contactHasMeaningfulData(contact));
  }
}

/**
 * Recursively prunes empty arrays, empty objects, null/undefined/blank strings from any FHIR payload
 * while preserving falsy-yet-meaningful primitives such as 0 or false.
 */
export function pruneFhirResource<T>(input: T): T {
  const isEmptyPrimitive = (v: any) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

  const prune = (val: any): any => {
    if (Array.isArray(val)) {
      return val
        .map(prune)
        .filter(item => {
          if (Array.isArray(item)) return item.length > 0;
          if (item && typeof item === 'object') return Object.keys(item).length > 0;
          return !isEmptyPrimitive(item);
        });
    }

    if (val && typeof val === 'object') {
      const result: any = {};
      for (const [k, v] of Object.entries(val)) {
        const prunedValue = prune(v);
        if (typeof prunedValue === 'number' || typeof prunedValue === 'boolean') {
          result[k] = prunedValue;
          continue;
        }
        if (Array.isArray(prunedValue)) {
          if (prunedValue.length > 0) result[k] = prunedValue;
          continue;
        }
        if (prunedValue && typeof prunedValue === 'object') {
          if (Object.keys(prunedValue).length > 0) {
            result[k] = prunedValue;
          }
          continue;
        }
        if (!isEmptyPrimitive(prunedValue)) {
          result[k] = prunedValue;
        }
      }
      return result;
    }

    return val;
  };

  return prune(input);
}

function sanitizeContactEntry(contact: any): any {
  if (!contact) {
    return contact;
  }

  if (Array.isArray(contact.telecom)) {
    contact.telecom = contact.telecom.filter((tp: any) => hasContactPointValue(tp?.value));
  }

  return contact;
}

function contactHasMeaningfulData(contact: any): boolean {
  if (!contact) {
    return false;
  }

  const hasRelationship = Array.isArray(contact.relationship) && contact.relationship.length > 0;
  const hasTelecom = Array.isArray(contact.telecom) && contact.telecom.length > 0;
  const hasAddress = Array.isArray(contact.address)
    ? contact.address.length > 0
    : !!(contact.address && Object.keys(contact.address).length > 0);
  const hasOrganization = contact.organization && hasContactPointValue(contact.organization.reference);
  const hasPeriod = !!contact.period;
  const hasGender = hasContactPointValue(contact.gender);
  const hasName = !!(
    contact.name &&
    (
      hasContactPointValue(contact.name.family) ||
      (Array.isArray(contact.name.given) && contact.name.given.some((g: any) => hasContactPointValue(g)))
    )
  );

  return hasRelationship || hasTelecom || hasAddress || hasOrganization || hasPeriod || hasGender || hasName;
}

function hasContactPointValue(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return true;
}
